<div class="w-full flex justify-center">
  <div class="max-w-[1800px] w-full">
    <zaeper-page-layout [title]="'Entwicklerlizenz'" [pageLayout]="ePageLayout.CARDS" [navigation]="navigation">
      <ng-template zTemplate="header">
        <zaeper-icon fontSet="material-icons-outlined">card_membership</zaeper-icon>
      </ng-template>

      <div class="flex flex-col gap-6">
        <p-card>
          <ng-template pTemplate="header">
            <div class="px-4 pt-4 flex justify-between items-center">
              <div>
                <h3 class="text-lg font-semibold m-0">Lizenzstatus</h3>
                <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">Deine aktuelle Entwicklerlizenz</p>
              </div>
            </div>
          </ng-template>

          @if (isLoading) {
            <div class="flex items-center justify-center py-8 text-surface-400 dark:text-surface-500">
              <i class="pi pi-spinner pi-spin text-2xl"></i>
            </div>
          } @else if (activeLicense) {
            <div class="flex flex-col gap-4">
              <div class="flex items-center gap-3">
                <p-tag [value]="getStatusLabel(activeLicense.status)" [severity]="getStatusSeverity(activeLicense.status)"/>
                <span class="text-sm text-surface-500 dark:text-surface-400">
                  Laufzeit: {{ formatDate(activeLicense.currentPeriodStart) }} - {{ formatDate(activeLicense.currentPeriodEnd) }}
                </span>
              </div>

              @if (activeLicense.cancelAtPeriodEnd) {
                <div class="p-3 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                  Die Lizenz wird zum {{ formatDate(activeLicense.currentPeriodEnd) }} gekündigt.
                </div>
              }

              <div class="flex gap-2">
                @if (!activeLicense.cancelAtPeriodEnd) {
                  <p-button
                    label="Lizenz kündigen"
                    severity="danger"
                    [outlined]="true"
                    [rounded]="true"
                    (onClick)="cancelLicense()"/>
                }
              </div>
            </div>
          } @else if (isSubscribing) {
            <div class="flex flex-col gap-4">
              <div class="p-4 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="font-semibold text-surface-900 dark:text-surface-0">Entwicklerlizenz</div>
                    <div class="text-sm text-surface-500">Jährliches Abonnement</div>
                    <app-licence-price />
                  </div>
                  <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <i class="pi pi-verified text-primary"></i>
                  </div>
                </div>
              </div>

              @if (paymentErrorMessage) {
                <div class="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {{ paymentErrorMessage }}
                </div>
              }

              <div #paymentElement class="min-h-[200px]">
                @if (isLoadingPayment) {
                  <div class="flex items-center justify-center h-[200px] text-surface-400 dark:text-surface-500">
                    <i class="pi pi-spinner pi-spin text-2xl"></i>
                  </div>
                }
              </div>

              <div class="flex gap-2">
                <p-button
                  label="Abbrechen"
                  severity="secondary"
                  [outlined]="true"
                  [rounded]="true"
                  (onClick)="cancelSubscribe()"/>
                <p-button
                  label="Lizenz aktivieren"
                  icon="pi pi-lock"
                  [rounded]="true"
                  [loading]="isConfirmingPayment"
                  [disabled]="isLoadingPayment || !!paymentErrorMessage"
                  (onClick)="confirmPayment()"/>
              </div>
            </div>
          } @else {
            <div class="text-center py-6">
              <p class="text-surface-500 dark:text-surface-400 mb-4">Du hast derzeit keine aktive Entwicklerlizenz.</p>
              <p class="text-sm text-surface-400 dark:text-surface-500 mb-4">Eine Entwicklerlizenz wird benötigt, um Apps, Widgets und Launcher zu veröffentlichen.</p>
              <p-button
                label="Lizenz abonnieren"
                icon="pi pi-credit-card"
                [rounded]="true"
                (onClick)="subscribe()"/>
            </div>
          }
        </p-card>

        <p-card>
          <ng-template pTemplate="header">
            <div class="px-4 pt-4 flex justify-between items-center">
              <div>
                <h3 class="text-lg font-semibold m-0">Zahlungsmethoden</h3>
                <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">Verwalte deine Zahlungsmethoden</p>
              </div>
              <p-button
                label="Hinzufügen"
                icon="pi pi-plus"
                [rounded]="true"
                [outlined]="true"
                (onClick)="addPaymentMethod()"/>
            </div>
          </ng-template>

          <p-table [value]="paymentMethods" [loading]="isLoading" [tableStyle]="{ 'min-width': '100%' }">
            <ng-template pTemplate="body" let-method>
              <tr>
                <td>
                  <div class="flex items-center gap-2">
                    <span class="font-medium capitalize">{{ method.cardBrand }}</span>
                    <span class="text-surface-500 dark:text-surface-400">**** {{ method.cardLastFour }}</span>
                    @if (method.isDefault) {
                      <p-tag value="Standard" severity="info" [rounded]="true"/>
                    }
                  </div>
                </td>
                <td class="text-surface-500 dark:text-surface-400">{{ method.cardExpMonth }}/{{ method.cardExpYear }}</td>
                <td class="text-right">
                  @if (!method.isDefault) {
                    <p-button
                      icon="pi pi-star"
                      [outlined]="true"
                      [rounded]="true"
                      [text]="true"
                      pTooltip="Als Standard setzen"
                      (onClick)="setDefaultPaymentMethod(method)"/>
                  }
                  <p-button
                    icon="pi pi-trash"
                    [outlined]="true"
                    [rounded]="true"
                    [text]="true"
                    severity="danger"
                    pTooltip="Entfernen"
                    (onClick)="deletePaymentMethod(method)"/>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" class="text-center py-8 text-surface-500 dark:text-surface-400">
                  Keine Zahlungsmethoden vorhanden
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </div>
    </zaeper-page-layout>
  </div>
</div>
